import React, { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import styles from "./trend-line-editor.module.scss";
import { useI18N } from "@src/language/language-context";
import {
  GenericWidgetSemantics,
  Widget,
  WidgetSemantics,
} from "@src/types/dashboards";
import { MachineNameAndSourceId } from "@common/types/server-list-types";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitOptionLabelOf } from "@src/cloud-connect/units/utils/unit-option-label-of";
import type { UnitDefinition } from "@src/types/units";
import {
  selectUnitsById,
  selectUnitsStatus,
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";
import {
  buildTrendLineQuery,
  getWidgetDataSource,
  getWidgetWindow,
} from "@src/components/organisms/widgets/widget-query";
import {
  AGGREGATED_POLLING_LABELS,
  buildPollingOptions,
  DEFAULT_POLLING_INTERVAL,
  durationToIso,
  findPollingOption,
  isoToDurationMs,
  LINE_CHART_DURATIONS_MS,
  LINE_CHART_GRANULARITIES,
} from "@src/components/organisms/widgets/constants";
import {
  getNumericNodeUnit,
  toNumericNodeOptions,
} from "@src/components/organisms/widgets/node-options";
import {
  buildScaleTransforms,
  scaleForField,
  scaleTransformsKey,
} from "@src/components/organisms/widgets/utils/value-scaling";
import { parseUserNumber } from "@src/components/organisms/widgets/utils/parse-user-number";
import { axisBoundsInputError } from "@src/components/organisms/widgets/axis-bounds";
import { buildDurationOptions } from "@src/components/organisms/widgets/utils/build-duration-options";
import { findDurationOption } from "@src/components/organisms/widgets/utils/find-duration-options";
import { MachineNodeUnitFields } from "@src/components/molecules/machine-node-unit-fields/machine-node-unit-fields";
import { ScaleInput } from "@src/components/atoms/scale-input/scale-input";
import { DashboardEditWidgetDrawerDropdown } from "@src/components/atoms/dashboard-edit-widget-drawer-dropdown/dashboard-edit-widget-drawer-dropdown";
import { DatePicker } from "@src/components/atoms/date-picker/date-picker";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import {
  ColorSwatchPicker,
  DEFAULT_TREND_LINE_COLOR,
  pickPaletteHex,
  TREND_LINE_PALETTE,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { ThresholdFields } from "@src/components/molecules/threshold-fields/threshold-fields";
import { ManualYRangeFields } from "@src/components/molecules/manual-y-range-fields/manual-y-range-fields";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import Spinner from "@src/components/atoms/spinner/spinner";
import infoIconFilled from "@src/assets/svg/info-icon-filled.svg";
import { useSaveWidget } from "../hooks/use-save-widget";

const cssClassName = "trend-line-editor";

export interface TrendLineEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

/**
 * Editor for a `trend-line` widget: the machine / node / unit it reads, the
 * raw-or-aggregated sampling and its bucket size, the window (a rolling
 * duration or an absolute timeframe), the warning / critical thresholds, the
 * aggregated min/max display toggles, the line colour, the optional manual Y
 * range and the value scale. Owns its own state and its own save.
 */
export const TrendLineEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: TrendLineEditorProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const units = useSelector(selectUnitsById);
  const unitsStatus = useSelector(selectUnitsStatus);
  const isLoadingUnits = unitsStatus === "idle" || unitsStatus === "loading";

  const savedSemantics = widget.semantics as Partial<WidgetSemantics> & {
    target_manual?: number | null;
    min_value?: number | null;
    max_value?: number | null;
    thresholds?: { warning?: number | null; critical?: number | null } | null;
  };
  const savedBounds = widget.semantics as Partial<GenericWidgetSemantics>;
  const savedDataSource = useMemo(
    () => getWidgetDataSource(widget.query),
    [widget.query],
  );
  const savedWindow = getWidgetWindow(widget.query);
  const savedDurationMs = isoToDurationMs(savedWindow?.duration);
  // A stored duration wins over a stored timeframe — the two are mutually
  // exclusive and the payload only ever carries one of them.
  const savedFrom = savedDurationMs == null ? savedWindow?.from ?? null : null;
  const savedTo = savedDurationMs == null ? savedWindow?.to ?? null : null;
  const savedFrequency = widget.query?.sampling?.frequency ?? null;
  // Pending widgets ignore the template's unit; an existing one keeps it.
  const savedUnitId = isPendingWidget
    ? null
    : widget.presentation?.unit ?? null;

  const [title, setTitle] = useState(widget.title);
  const [machine, setMachine] = useState<MachineNameAndSourceId | null>(
    savedDataSource
      ? { name: savedDataSource.group, source_id: savedDataSource.group }
      : null,
  );
  const [node, setNode] = useState<MachineNameAndSourceId | null>(
    savedDataSource
      ? { name: savedDataSource.source, source_id: savedDataSource.source }
      : null,
  );
  const [unit, setUnit] = useState<UnitDefinition | null>(() =>
    getUnitById(units, savedUnitId),
  );
  // Auto-pick keeps re-evaluating on every node change until save; a manual
  // pick (handleUnitSelection) is the only thing that turns it off.
  const [shouldAutoPickUnit, setShouldAutoPickUnit] = useState(
    savedUnitId == null,
  );
  const [min, setMin] = useState(
    savedSemantics.min_value != null ? String(savedSemantics.min_value) : "",
  );
  const [max, setMax] = useState(
    savedSemantics.max_value != null ? String(savedSemantics.max_value) : "",
  );
  const [manualYRange, setManualYRange] = useState(
    savedSemantics.min_value != null && savedSemantics.max_value != null,
  );
  const [minEnabled, setMinEnabled] = useState(savedBounds.show_min === true);
  const [maxEnabled, setMaxEnabled] = useState(savedBounds.show_max === true);
  const [warningEnabled, setWarningEnabled] = useState(
    savedSemantics.thresholds?.warning != null,
  );
  const [warningValue, setWarningValue] = useState(
    savedSemantics.thresholds?.warning != null
      ? String(savedSemantics.thresholds.warning)
      : "",
  );
  const [criticalEnabled, setCriticalEnabled] = useState(
    savedSemantics.thresholds?.critical != null,
  );
  const [criticalValue, setCriticalValue] = useState(
    savedSemantics.thresholds?.critical != null
      ? String(savedSemantics.thresholds.critical)
      : "",
  );
  const [isAggregated, setIsAggregated] = useState(
    savedFrequency != null && savedFrequency !== "raw",
  );
  const [frequency, setFrequency] = useState<string | null>(savedFrequency);
  const [isTimeframe, setIsTimeframe] = useState(
    savedFrom != null || savedTo != null,
  );
  const [fromDate, setFromDate] = useState<string | null>(savedFrom);
  const [toDate, setToDate] = useState<string | null>(savedTo);
  const [durationMs, setDurationMs] = useState<number | null>(
    savedDurationMs ?? LINE_CHART_DURATIONS_MS[0].ms,
  );
  const [pollingInterval, setPollingInterval] = useState<string | null>(
    widget.query?.sampling?.polling_interval ?? DEFAULT_POLLING_INTERVAL,
  );
  // A brand-new widget opens on a random palette colour. An existing one keeps
  // its colour, or — saved before colours existed — the app default, which is
  // exactly the colour its chart draws today.
  const [color, setColor] = useState<string | null>(
    widget.presentation?.color ??
      (isPendingWidget ? pickPaletteHex() : DEFAULT_TREND_LINE_COLOR),
  );
  const [valueScale, setValueScale] = useState(
    String(scaleForField("value", widget.encoding?.transforms)),
  );
  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [numericNodeOptions, setNumericNodeOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [nodeUnits, setNodeUnits] = useState<Record<string, number | null>>({});
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const texts = {
    saveButton: i18n("modules.trendLine.saveButton"),
    cancelButton: i18n("modules.trendLine.cancelButton"),
    titleLabel: i18n("modules.trendLine.titleLabel"),
    titleInputPlaceholder: i18n("modules.trendLine.titleInputPlaceholder"),
    machineLabel: i18n("modules.trendLine.machineLabel"),
    noMachines: i18n("modules.trendLine.noMachines"),
    machinesPlaceholder: i18n("modules.trendLine.machinesPlaceholder"),
    nodeLabel: i18n("modules.trendLine.nodeLabel"),
    noNodes: i18n("modules.trendLine.noNodes"),
    nodesPlaceholder: i18n("modules.trendLine.nodesPlaceholder"),
    unitLabel: i18n("modules.trendLine.unitLabel"),
    noUnits: i18n("modules.trendLine.noUnits"),
    unitsPlaceholder: i18n("modules.trendLine.unitsPlaceholder"),
    frequencyLabel: i18n("modules.trendLine.frequencyLabel"),
    rawData: i18n("modules.trendLine.rawData"),
    aggregatedData: i18n("modules.trendLine.aggregatedData"),
    granularityLabel: i18n("modules.trendLine.granularityLabel"),
    noGranularities: i18n("modules.trendLine.noGranularities"),
    granularityPlaceholder: i18n("modules.trendLine.granularityPlaceholder"),
    timeRangeLabel: i18n("modules.trendLine.timeRangeLabel"),
    lookbackLabel: i18n("modules.trendLine.lookbackLabel"),
    timeframeLabel: i18n("modules.trendLine.timeframeLabel"),
    startLabel: i18n("modules.trendLine.startLabel"),
    endLabel: i18n("modules.trendLine.endLabel"),
    noLookbacks: i18n("modules.trendLine.noLookbacks"),
    lookbackPlaceholder: i18n("modules.trendLine.lookbackPlaceholder"),
    pollingIntervalLabel: i18n("modules.trendLine.pollingIntervalLabel"),
    pollingInfoText: i18n("modules.trendLine.pollingInfoText"),
    noPollingIntervals: i18n("modules.trendLine.noPollingIntervals"),
    pollingIntervalPlaceholder: i18n(
      "modules.trendLine.pollingIntervalPlaceholder",
    ),
    thresholdLabel: i18n("modules.trendLine.thresholdLabel"),
    warningLabel: i18n("modules.trendLine.warningLabel"),
    criticalLabel: i18n("modules.trendLine.criticalLabel"),
    aggregateBoundsLabel: i18n("modules.trendLine.aggregateBoundsLabel"),
    showMinLabel: i18n("modules.trendLine.showMinLabel"),
    showMaxLabel: i18n("modules.trendLine.showMaxLabel"),
    colorLabel: i18n("modules.trendLine.colorLabel"),
    manualYRangeLabel: i18n("modules.trendLine.manualYRangeLabel"),
    yMinLabel: i18n("modules.trendLine.yMinLabel"),
    yMaxLabel: i18n("modules.trendLine.yMaxLabel"),
    resetYRangeLabel: i18n("modules.trendLine.resetYRangeLabel"),
    titleRequired: i18n("modules.trendLine.titleRequired"),
    machineRequired: i18n("modules.trendLine.machineRequired"),
    nodeRequired: i18n("modules.trendLine.nodeRequired"),
    timeframeRequired: i18n("modules.trendLine.timeframeRequired"),
    durationRequired: i18n("modules.trendLine.durationRequired"),
    frequencyRequired: i18n("modules.trendLine.frequencyRequired"),
    yAxisBoundsRequired: i18n("modules.trendLine.yAxisBoundsRequired"),
    yAxisRangeInvalid: i18n("modules.trendLine.yAxisRangeInvalid"),
    warningValueRequired: i18n("modules.trendLine.warningValueRequired"),
    criticalValueRequired: i18n("modules.trendLine.criticalValueRequired"),
    thresholdOrderInvalid: i18n("modules.trendLine.thresholdOrderInvalid"),
    scaleInvalid: i18n("modules.trendLine.scaleInvalid"),
  };

  const unitOptions = Object.entries(units).map(([key, value]) => {
    const text =
      value.unit_text?.eng ?? Object.values(value.unit_text ?? {})[0];
    const label = unitOptionLabelOf(text, value.unit_id);
    return {
      name: label,
      searchText: [
        value.unit_id,
        value.external_reference,
        text?.name,
        text?.symbol,
        text?.description,
      ]
        .filter(Boolean)
        .join(" "),
      source_id: key,
    };
  });
  const selectedUnitEntry = unit
    ? Object.entries(units).find(
        ([key, candidate]) =>
          key === unit.unit_id || candidate.unit_id === unit.unit_id,
      )
    : undefined;
  const selectedUnitOption = unit
    ? {
        name: unitOptionLabelOf(unit.unit_text?.eng, unit.unit_id),
        source_id: selectedUnitEntry?.[0] ?? unit.unit_id,
      }
    : null;
  const findUnitByReference = (reference: number | string | null) =>
    reference == null
      ? undefined
      : Object.values(units).find(
          (u) => u.external_reference === String(reference),
        );

  // Pick = lock (flip the auto-pick flag off), clear = release (flip it on).
  // Auto-pick writes below do NOT change the flag — writing isn't picking.
  const handleUnitSelection = (selected: MachineNameAndSourceId | null) => {
    setUnit(
      selected
        ? units[selected.source_id] ??
            Object.values(units).find(
              (candidate) => candidate.unit_id === selected.source_id,
            ) ??
            null
        : null,
    );
    setShouldAutoPickUnit(selected == null);
  };

  // Once both machines and the initial node load are complete, reveal the form.
  useEffect(() => {
    if (!isInitializing) return;
    if (
      !isLoadingMachines &&
      !isLoadingUnits &&
      !(machine !== null && isLoadingNodes)
    ) {
      setIsInitializing(false);
    }
  }, [isLoadingMachines, isLoadingNodes, unitsStatus, machine, isInitializing]);

  // Load machines once.
  useEffect(() => {
    setIsLoadingMachines(true);
    getAllSourcesListUseCase().then((result) => {
      if (result.success && result.data) {
        setMachineOptions(
          Object.values(result.data).map((s) => ({
            name: s.name,
            source_id: s.source_id,
          })),
        );
      } else {
        setMachineOptions([]);
        console.error("Failed to load machines:", result.error);
      }
      setIsLoadingMachines(false);
    });
  }, []);

  // Units come from the global slice (loaded at dashboards-load). Lazy
  // fallback: if the slice was never attempted, fetch once so the dropdown
  // isn't empty.
  const unitsAttempted = useRef(false);
  useEffect(() => {
    if (unitsStatus !== "idle" || unitsAttempted.current) return;
    unitsAttempted.current = true;
    dispatch(unitsRequested());
    getUnitsUseCase().then((result) => {
      if (result.success && result.data) {
        dispatch(unitsLoaded(result.data));
      } else {
        dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
      }
    });
  }, [unitsStatus, dispatch]);

  // The sources list loads asynchronously, so the prefill hands the machine id
  // forward and this effect swaps in the real name once the options arrive.
  useEffect(() => {
    if (machineOptions.length === 0) return;
    const machineId = savedDataSource?.group ?? widget.query?.source;
    if (!machineId) return;
    const match = machineOptions.find((m) => m.source_id === machineId);
    if (!match) return;
    setMachine((prev) => {
      if (!prev) return prev;
      // If the user is currently looking at a DIFFERENT machine, do NOT
      // overwrite — the source_id test stops that race.
      if (prev.source_id !== match.source_id) return prev;
      if (prev.name === match.name) return prev;
      return { name: match.name, source_id: match.source_id };
    });
  }, [machineOptions, savedDataSource, widget.query?.source]);

  // Same raw-id → human-name swap for the node: without it, re-opening a
  // trend-line widget showed the raw node id in the dropdown
  // ("ns=1;s=Temperature") instead of the human name ("Temperature").
  useEffect(() => {
    if (numericNodeOptions.length === 0) return;
    if (!savedDataSource) return;
    const match = numericNodeOptions.find(
      (n) => n.source_id === savedDataSource.source,
    );
    if (!match) return;
    setNode((prev) => {
      if (!prev) return prev;
      if (prev.source_id !== match.source_id) return prev;
      if (prev.name === match.name) return prev;
      return { name: match.name, source_id: match.source_id };
    });
  }, [numericNodeOptions, savedDataSource]);

  // Load nodes when the machine is selected.
  useEffect(() => {
    if (!machine) {
      setNumericNodeOptions([]);
      setNodeUnits({});
      setNode(null);
      return;
    }
    setIsLoadingNodes(true);
    let cancelled = false;
    getActiveTimeseriesUseCase(machine.source_id).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        // Only numeric nodes are consumable by a line chart.
        setNumericNodeOptions(toNumericNodeOptions(result.data.timeseries));
        setNodeUnits(
          Object.fromEntries(
            Object.values(result.data.timeseries)
              .filter((timeseries) => timeseries.data_type === "numeric")
              .map((timeseries) => [
                timeseries.source,
                getNumericNodeUnit(result.data.timeseries, timeseries.source),
              ]),
          ),
        );
        // The stored node belongs to the stored machine: restore it when that
        // machine's options load, leave the node empty for any other machine.
        const ds = getWidgetDataSource(widget.query);
        if (ds && ds.group === machine.source_id && ds.source) {
          setNode({ name: ds.source, source_id: ds.source });
        }
      } else {
        setNumericNodeOptions([]);
        setNodeUnits({});
        console.error("Failed to load nodes:", result.error);
      }
      setIsLoadingNodes(false);
    });

    return () => {
      cancelled = true;
    };
  }, [machine, widget.query]);

  // Restore the saved unit once the catalog is available (the global load may
  // still be in flight when the editor opens).
  useEffect(() => {
    if (isPendingWidget) return;
    const id = widget.presentation?.unit;
    if (id == null) return;
    const match = getUnitById(units, id);
    if (match) setUnit(match);
  }, [units, widget.presentation?.unit, isPendingWidget]);

  // On first open, use the selected node's unit only when the widget has no
  // saved unit. A later null node unit never clears the current selection.
  useEffect(() => {
    if (!shouldAutoPickUnit || !node) return;
    const nodeUnit = nodeUnits[node.source_id];
    if (nodeUnit == null) return;
    const match = findUnitByReference(nodeUnit);
    if (match) {
      setUnit(match);
    }
  }, [node, nodeUnits, units, shouldAutoPickUnit]);

  const validate = (): string | null => {
    if (title.trim() === "") return texts.titleRequired;
    if (!machine) return texts.machineRequired;
    if (!node) return texts.nodeRequired;
    // The window is answered either by an absolute timeframe or by a rolling
    // duration preset. A cleared duration answers nothing.
    if (isTimeframe) {
      if (!fromDate || !toDate) return texts.timeframeRequired;
    } else if (durationMs == null) {
      return texts.durationRequired;
    }
    if (isAggregated && (frequency == null || frequency === "")) {
      return texts.frequencyRequired;
    }
    // Manual Y range: both ends required, and min must stay below max.
    if (manualYRange) {
      const boundsError = axisBoundsInputError(min, max);
      if (boundsError === "required") return texts.yAxisBoundsRequired;
      if (boundsError === "invalid") return texts.yAxisRangeInvalid;
    }
    if (warningEnabled && parseUserNumber(warningValue) == null) {
      return texts.warningValueRequired;
    }
    if (criticalEnabled && parseUserNumber(criticalValue) == null) {
      return texts.criticalValueRequired;
    }
    if (
      warningEnabled &&
      criticalEnabled &&
      parseUserNumber(warningValue) != null &&
      parseUserNumber(criticalValue) != null &&
      (parseUserNumber(warningValue) as number) >
        (parseUserNumber(criticalValue) as number)
    ) {
      return texts.thresholdOrderInvalid;
    }
    if (valueScale.trim() !== "" && parseUserNumber(valueScale) === null) {
      return texts.scaleInvalid;
    }
    return null;
  };

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    // Each enabled threshold contributes its key; disabled/removed thresholds
    // are dropped. Both off → null.
    const thresholds: { warning?: number; critical?: number } = {};
    if (warningEnabled && parseUserNumber(warningValue) != null) {
      thresholds.warning = parseUserNumber(warningValue) as number;
    }
    if (criticalEnabled && parseUserNumber(criticalValue) != null) {
      thresholds.critical = parseUserNumber(criticalValue) as number;
    }
    const thresholdsOut =
      Object.keys(thresholds).length > 0 ? thresholds : null;

    // Round-trip every key the BE returned except `state_map`, which the
    // per-type models reject as `extra_forbidden`.
    const { state_map: _strippedStateMap, ...savedSemanticsRest } =
      widget.semantics as Record<string, unknown> & {
        state_map?: unknown;
      };

    const updated: Widget = {
      ...widget,
      title: title.trim() || widget.title,
      image_url: widget.image_url ?? null,
      query:
        machine && node
          ? buildTrendLineQuery({
              machineId: machine.source_id,
              nodeId: node.source_id,
              frequency: isAggregated ? frequency : "raw",
              // Raw-mode polling cadence from the dropdown; aggregated mode
              // ignores it (the fetch layer derives the cadence from the
              // bucket size, keeping the query model explicit).
              pollingInterval: isAggregated ? undefined : pollingInterval,
              // Duration persists symbolically (rolling at render); timeframe
              // persists resolved from/to dates. A cleared duration drops the
              // window entirely rather than persisting an unreadable PT0M.
              durationIso: isTimeframe
                ? null
                : durationMs != null
                ? durationToIso(durationMs)
                : null,
              from: isTimeframe ? fromDate : null,
              to: isTimeframe ? toDate : null,
            })
          : widget.query,
      semantics: {
        ...(savedSemanticsRest as WidgetSemantics),
        // No target input today: the stored value round-trips untouched.
        target_manual: savedSemantics.target_manual ?? null,
        min_value: manualYRange ? parseUserNumber(min) : null,
        max_value: manualYRange ? parseUserNumber(max) : null,
        thresholds: thresholdsOut,
        show_min: minEnabled || undefined,
        show_max: maxEnabled || undefined,
      } as WidgetSemantics,
      encoding: {
        ...widget.encoding,
        ...scaleTransformsKey(
          widget.encoding?.transforms,
          ["value"],
          buildScaleTransforms([{ field: "value", scale: valueScale }]),
        ),
      },
      presentation: {
        ...widget.presentation,
        unit: unit?.unit_id ?? null,
        color: color ?? DEFAULT_TREND_LINE_COLOR,
      },
    };

    save(updated, { validate, imageFile: null });
  };

  if (isInitializing) {
    return (
      <div className={styles[`${cssClassName}__loading`]}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles[`${cssClassName}__outer-wrapper`]}>
      <Input2025
        inputPlaceHolder={texts.titleInputPlaceholder}
        labelText={texts.titleLabel}
        inputValue={title}
        onChangeFunc={(e) => setTitle(e.target.value)}
        inputVariant="dark-background"
      />

      <MachineNodeUnitFields
        texts={{
          machineLabel: texts.machineLabel,
          noMachines: texts.noMachines,
          machinesPlaceholder: texts.machinesPlaceholder,
          nodeLabel: texts.nodeLabel,
          noNodes: texts.noNodes,
          nodesPlaceholder: texts.nodesPlaceholder,
          unitLabel: texts.unitLabel,
          noUnits: texts.noUnits,
          unitsPlaceholder: texts.unitsPlaceholder,
        }}
        machineOptions={machineOptions}
        selectedMachine={machine}
        onSelectMachine={(selected) => {
          setMachine(selected);
          setNode(null);
        }}
        isLoadingMachines={isLoadingMachines}
        nodeOptions={numericNodeOptions}
        selectedNode={node}
        onSelectNode={setNode}
        isLoadingNodes={isLoadingNodes}
        unitOptions={unitOptions}
        selectedUnit={selectedUnitOption}
        onSelectUnit={handleUnitSelection}
        isLoadingUnits={isLoadingUnits}
      />

      <div className={styles[`${cssClassName}__field`]}>
        <ScaleInput value={valueScale} onChange={setValueScale} />
      </div>

      <div>
        <span className={styles[`${cssClassName}__toggle-label`]}>
          {texts.frequencyLabel}
        </span>
        <div className={styles[`${cssClassName}__toggle`]}>
          <button
            type="button"
            className={cn(styles[`${cssClassName}__toggle-button`], {
              [styles[`${cssClassName}__toggle-button--active`]]: !isAggregated,
            })}
            onClick={() => setIsAggregated(false)}
          >
            {texts.rawData}
          </button>
          <button
            type="button"
            className={cn(styles[`${cssClassName}__toggle-button`], {
              [styles[`${cssClassName}__toggle-button--active`]]: isAggregated,
            })}
            onClick={() => {
              setIsAggregated(true);
              // Default "hourly" when switching into aggregated for the first
              // time, or when the previous pick was raw (out of context once
              // aggregated is on). Existing non-raw picks (e.g. daily, weekly)
              // are untouched — the user already made that choice.
              setFrequency((prev) =>
                prev == null || prev === "raw" ? "hourly" : prev,
              );
            }}
          >
            {texts.aggregatedData}
          </button>
        </div>
      </div>

      {isAggregated && (
        <div className={styles[`${cssClassName}__field`]}>
          <DashboardEditWidgetDrawerDropdown
            // The granularity dropdown is the aggregated mode's bucket
            // selector. `raw` is filtered out because it's out of context in
            // aggregated mode — the Raw/Aggregated toggle IS the raw
            // selection.
            options={LINE_CHART_GRANULARITIES.filter((g) => g !== "raw").map(
              (g) => ({ name: g, source_id: g }),
            )}
            selectedOption={
              frequency ? { name: frequency, source_id: frequency } : null
            }
            onSelectOption={(selected) =>
              setFrequency(selected?.source_id ?? null)
            }
            labelText={texts.granularityLabel}
            noOptionsText={texts.noGranularities}
            placeholderText={texts.granularityPlaceholder}
            isLoading={false}
          />
        </div>
      )}

      <div>
        <span className={styles[`${cssClassName}__toggle-label`]}>
          {texts.timeRangeLabel}
        </span>
        <div className={styles[`${cssClassName}__toggle`]}>
          <button
            type="button"
            className={cn(styles[`${cssClassName}__toggle-button`], {
              [styles[`${cssClassName}__toggle-button--active`]]: !isTimeframe,
            })}
            onClick={() => setIsTimeframe(false)}
          >
            {texts.lookbackLabel}
          </button>
          <button
            type="button"
            className={cn(styles[`${cssClassName}__toggle-button`], {
              [styles[`${cssClassName}__toggle-button--active`]]: isTimeframe,
            })}
            onClick={() => setIsTimeframe(true)}
          >
            {texts.timeframeLabel}
          </button>
        </div>
      </div>

      {isTimeframe ? (
        <div className={styles[`${cssClassName}__row`]}>
          <DatePicker
            selectedDate={fromDate}
            onDateChange={setFromDate}
            placeholder={texts.startLabel}
          />
          <DatePicker
            selectedDate={toDate}
            onDateChange={setToDate}
            placeholder={texts.endLabel}
          />
        </div>
      ) : (
        <div className={styles[`${cssClassName}__field`]}>
          <DashboardEditWidgetDrawerDropdown
            options={buildDurationOptions(LINE_CHART_DURATIONS_MS, i18n)}
            selectedOption={
              durationMs != null
                ? findDurationOption(LINE_CHART_DURATIONS_MS, durationMs, i18n)
                : null
            }
            onSelectOption={(selected) =>
              setDurationMs(selected ? Number(selected.source_id) : null)
            }
            labelText={texts.lookbackLabel}
            noOptionsText={texts.noLookbacks}
            placeholderText={texts.lookbackPlaceholder}
            isLoading={false}
          />
        </div>
      )}

      {/* Polling cadence (after lookback/timeframe): editable in raw mode;
          aggregated mode shows the bucket cadence locked, with an info
          tooltip explaining why. */}
      {isAggregated ? (
        <div className={styles[`${cssClassName}__field`]}>
          <span className={styles[`${cssClassName}__polling-label`]}>
            {texts.pollingIntervalLabel}
            <ToolTip content={texts.pollingInfoText} position="top">
              <img
                src={infoIconFilled}
                alt=""
                className={styles[`${cssClassName}__polling-info-icon`]}
              />
            </ToolTip>
          </span>
          <div className={styles[`${cssClassName}__polling-locked`]}>
            {AGGREGATED_POLLING_LABELS[frequency ?? ""] ?? frequency}
          </div>
        </div>
      ) : (
        <div className={styles[`${cssClassName}__field`]}>
          <DashboardEditWidgetDrawerDropdown
            options={buildPollingOptions(i18n)}
            selectedOption={
              pollingInterval ? findPollingOption(pollingInterval, i18n) : null
            }
            onSelectOption={(selected) =>
              // null (X) clears the selection — the default 30s applies again
              // at save time via the builder.
              setPollingInterval(selected?.source_id ?? null)
            }
            labelText={texts.pollingIntervalLabel}
            noOptionsText={texts.noPollingIntervals}
            placeholderText={texts.pollingIntervalPlaceholder}
            isLoading={false}
          />
        </div>
      )}

      {/* Each threshold is checkbox-gated: the value input shows only when
          enabled and is required then. Both off → thresholds null. */}
      <ThresholdFields
        texts={{
          thresholdLabel: texts.thresholdLabel,
          warningLabel: texts.warningLabel,
          criticalLabel: texts.criticalLabel,
        }}
        warningEnabled={warningEnabled}
        warningValue={warningValue}
        onWarningEnabledChange={setWarningEnabled}
        onWarningValueChange={setWarningValue}
        criticalEnabled={criticalEnabled}
        criticalValue={criticalValue}
        onCriticalEnabledChange={setCriticalEnabled}
        onCriticalValueChange={setCriticalValue}
      />

      {/* Aggregated-line bounds display toggles. Only meaningful while the
          chart is aggregated. */}
      {isAggregated && (
        <fieldset className={styles[`${cssClassName}__thresholds`]}>
          <legend className={styles[`${cssClassName}__field-label`]}>
            {texts.aggregateBoundsLabel}
          </legend>
          <div className={styles[`${cssClassName}__threshold-row`]}>
            <CheckBoxWithText
              text={texts.showMinLabel}
              checkboxState={minEnabled}
              handleCheckBoxClick={() => setMinEnabled((prev) => !prev)}
            />
          </div>
          <div className={styles[`${cssClassName}__threshold-row`]}>
            <CheckBoxWithText
              text={texts.showMaxLabel}
              checkboxState={maxEnabled}
              handleCheckBoxClick={() => setMaxEnabled((prev) => !prev)}
            />
          </div>
        </fieldset>
      )}

      <div className={styles[`${cssClassName}__field`]}>
        <ColorSwatchPicker
          label={texts.colorLabel}
          value={color}
          swatches={TREND_LINE_PALETTE}
          onChange={setColor}
        />
      </div>

      <ManualYRangeFields
        texts={{
          manualYRangeLabel: texts.manualYRangeLabel,
          yMinLabel: texts.yMinLabel,
          yMaxLabel: texts.yMaxLabel,
          resetYRangeLabel: texts.resetYRangeLabel,
        }}
        enabled={manualYRange}
        min={min}
        max={max}
        onEnabledChange={setManualYRange}
        onMinChange={setMin}
        onMaxChange={setMax}
        onReset={() => {
          setMin("");
          setMax("");
          setManualYRange(false);
        }}
      />

      <Spacer16 />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          // Cancel must NOT fire mid-save: it would dispatch
          // removePendingWidget while POST/PUT are still in flight and leak a
          // server-side orphan.
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
